import type { Interest } from '../types';
import { LEGAL_COOKIE_VERSION, LEGAL_PRIVACY_VERSION, LEGAL_TERMS_VERSION } from '../constants';

// In dev we use CRA proxy unless an explicit base URL is provided.
// Support both REACT_APP_API_BASE (preferred, may include "/api") and REACT_APP_API_URL (no "/api").
const EXPLICIT_BASE = (process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || '').trim();
const normalizeBase = (base: string) => {
	const b = base.replace(/\/$/, '');
	return b.endsWith('/api') ? b : `${b}/api`;
};

const isLocalhostHost = (host: string) => host === 'localhost' || host === '127.0.0.1' || host === '::1';

// Runtime fallback strategy:
// 1) explicit env var if provided
// 2) local dev without env => CRA proxy (/api)
// 3) deployed browser without env => same-origin /api
const runtimeHost = typeof window !== 'undefined' ? window.location.hostname : '';
const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const shouldUseProxy = !EXPLICIT_BASE && process.env.NODE_ENV === 'development' && isLocalhostHost(runtimeHost);
const derivedBase = EXPLICIT_BASE
	? normalizeBase(EXPLICIT_BASE)
	: (runtimeOrigin ? normalizeBase(runtimeOrigin) : 'http://127.0.0.1:5000/api');
const API_BASE = shouldUseProxy ? '' : derivedBase;
const API_PREFIX = shouldUseProxy ? '/api' : '';
const BASE_WITHOUT_API = EXPLICIT_BASE ? EXPLICIT_BASE.replace(/\/$/, '').replace(/\/api$/, '') : '';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

type RequestOptions = {
	method?: HttpMethod;
	body?: any;
	auth?: boolean;
	headers?: Record<string, string>;
	signal?: AbortSignal;
	cacheTtlMs?: number;
	dedupe?: boolean;
};

const responseCache = new Map<string, { expiresAt: number; value: unknown }>();
const inFlightGetRequests = new Map<string, Promise<unknown>>();
const CACHE_MISS = Symbol('cache-miss');

// --- Global auth event handlers ---------------------------------------------
// This module can't touch React/router state directly, so AuthContext registers
// handlers here. A 401 on an authenticated request (with a token present) means
// the session expired mid-use; a 428 means legal re-acceptance is required.
type LegalVersions = { termsVersion: string; privacyVersion: string; cookieVersion: string } | undefined;
let onUnauthorizedHandler: (() => void) | null = null;
let onLegalRequiredHandler: ((currentVersions: LegalVersions) => void) | null = null;

export function setAuthHandlers(handlers: {
	onUnauthorized?: () => void;
	onLegalRequired?: (currentVersions: LegalVersions) => void;
}) {
	if (handlers.onUnauthorized) onUnauthorizedHandler = handlers.onUnauthorized;
	if (handlers.onLegalRequired) onLegalRequiredHandler = handlers.onLegalRequired;
}

function getAuthCacheScope() {
	const token = localStorage.getItem('dragonsframe_token') || 'anon';
	return token.slice(-24);
}

function buildGetCacheKey(path: string, options: RequestOptions) {
	const scope = options.auth ? `auth:${getAuthCacheScope()}` : 'public';
	return `${scope}:GET:${path}`;
}

function readCachedResponse<T>(key: string): T | typeof CACHE_MISS {
	const entry = responseCache.get(key);
	if (!entry) return CACHE_MISS;
	if (entry.expiresAt <= Date.now()) {
		responseCache.delete(key);
		return CACHE_MISS;
	}
	return entry.value as T;
}

function writeCachedResponse(key: string, value: unknown, ttlMs: number) {
	if (ttlMs <= 0) return;
	responseCache.set(key, {
		expiresAt: Date.now() + ttlMs,
		value,
	});
}

export function invalidateApiGetCacheByPath(pathPrefix: string) {
	for (const key of Array.from(responseCache.keys())) {
		if (key.includes(`:GET:${pathPrefix}`)) {
			responseCache.delete(key);
		}
	}
	for (const key of Array.from(inFlightGetRequests.keys())) {
		if (key.includes(`:GET:${pathPrefix}`)) {
			inFlightGetRequests.delete(key);
		}
	}
}

function invalidateApiGetCacheByPrefixes(pathPrefixes: string[]) {
	for (const prefix of pathPrefixes) {
		invalidateApiGetCacheByPath(prefix);
	}
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
	const token = localStorage.getItem('dragonsframe_token');
	const method = options.method || 'GET';
	const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options.headers || {}) };
	if (options.auth && token) headers['Authorization'] = `Bearer ${token}`;

	const cacheTtlMs = method === 'GET' ? Math.max(0, options.cacheTtlMs ?? 0) : 0;
	const shouldUseCache = cacheTtlMs > 0;
	const cacheKey = shouldUseCache ? buildGetCacheKey(path, options) : '';
	if (shouldUseCache && cacheKey) {
		const cached = readCachedResponse<T>(cacheKey);
		if (cached !== CACHE_MISS) return cached;
	}

	const shouldDedupe = method === 'GET' && !!options.dedupe && !options.signal;
	if (shouldDedupe && cacheKey) {
		const inFlight = inFlightGetRequests.get(cacheKey);
		if (inFlight) return inFlight as Promise<T>;
	}

	const executeRequest = async (): Promise<T> => {
		const res = await fetch(`${API_BASE}${API_PREFIX}${path}`, {
			method,
			headers,
			body: options.body ? JSON.stringify(options.body) : undefined,
			signal: options.signal,
			credentials: 'include',
		});

		// On non-OK, try to read text for better error messages
		if (!res.ok) {
			const errText = await res.text().catch(() => '');
			let maybeJson: any = null;
			if (errText) {
				try {
					maybeJson = JSON.parse(errText);
				} catch {
					maybeJson = null;
				}
			}
			const rawMsg = maybeJson?.error || maybeJson?.message || errText || `HTTP ${res.status}: ${res.statusText}`;
			const msg = (rawMsg === 'beta_whitelist_only' || rawMsg === 'forbidden_email')
				? 'Only allowed beta testers at the moment.'
				: rawMsg === 'no_tenant_for_email_domain'
				? "Dragonsframe isn't available for your school's email domain yet."
				: rawMsg;
			if (res.status === 401) {
				// Session expired/invalid mid-use (we sent a token but the server rejected it).
				if (options.auth && token) onUnauthorizedHandler?.();
				throw Object.assign(new Error('unauthorized'), { code: 401, info: maybeJson });
			}
			if (res.status === 428) {
				// Legal re-acceptance required; route the user to the acceptance screen.
				onLegalRequiredHandler?.(maybeJson?.currentVersions);
				throw Object.assign(new Error(msg), { code: 428, info: maybeJson });
			}
			throw Object.assign(new Error(msg), { code: res.status, info: maybeJson });
		}

		// 204 No Content should not attempt to parse a body
		if (res.status === 204) {
			// cast as T to preserve existing call sites' typing expectations
			return null as unknown as T;
		}

		// Some endpoints may return empty body; read text then JSON-parse if present.
		// If an HTML error page leaks through, avoid throwing a SyntaxError and surface a useful HTTP error instead.
		const text = await res.text();
		if (!text) return null as unknown as T;
		try {
			return JSON.parse(text) as T;
		} catch {
			const preview = text.slice(0, 120).replace(/\s+/g, ' ').trim();
			throw Object.assign(new Error(`Invalid JSON response from API (${res.status}): ${preview}`), { code: res.status || 500 });
		}
	};

	const requestPromise = executeRequest();
	if (shouldDedupe && cacheKey) {
		inFlightGetRequests.set(cacheKey, requestPromise as Promise<unknown>);
	}

	try {
		const data = await requestPromise;
		if (shouldUseCache && cacheKey) {
			writeCachedResponse(cacheKey, data, cacheTtlMs);
		}
		return data;
	} finally {
		if (shouldDedupe && cacheKey) {
			inFlightGetRequests.delete(cacheKey);
		}
	}
}

export function buildApiUrl(path: string) {
	return `${API_BASE}${API_PREFIX}${path}`;
}

// Auth
export async function apiRegister(data: {
	email: string;
	password: string;
	firstName: string;
	lastName: string;
	major: string;
	year: number;
	hometown: string;
	legalAcceptance: {
		agree: boolean;
		termsVersion: string;
		privacyVersion: string;
		cookieVersion: string;
	};
	cookieConsent?: {
		analytics: boolean;
		marketing: boolean;
	};
}): Promise<{ user?: any; token?: string; requiresVerification?: boolean; email?: string }> {
	return request('/auth/register', { method: 'POST', body: data });
}

export async function apiLogin(data: { email: string; password: string }): Promise<{ user: any; token: string }> {
	return request('/auth/login', { method: 'POST', body: data });
}

export async function apiVerifyEmail(email: string, code: string): Promise<{ user?: any; token?: string }> {
	return request('/auth/verify-email', { method: 'POST', body: { email, code } });
}

export async function apiResendCode(email: string): Promise<{ success: boolean }> {
	return request('/auth/resend-code', { method: 'POST', body: { email } });
}

export async function apiForgotPassword(email: string): Promise<{ success: boolean }> {
	return request('/auth/forgot-password', { method: 'POST', body: { email } });
}

export async function apiResetPassword(token: string, password: string): Promise<{ user?: any; token?: string }> {
	return request('/auth/reset-password', { method: 'POST', body: { token, password } });
}

// Public aggregate platform stats for the auth-screen social proof. Cached briefly.
export type PlatformStats = { courses: number; professors: number; colleges: number };
export async function apiGetStats(): Promise<PlatformStats> {
	return request('/stats', { cacheTtlMs: 5 * 60 * 1000 });
}

export type College = {
	slug: string;
	name: string;
	emailDomains: string[];
	primaryColor?: string | null;
};

// Public list of colleges (tenants) for the signup picker + branding. Cached briefly.
// Throws on a real network/server failure so callers can surface a retry affordance;
// a successful-but-empty response resolves to [] (handled as "no campuses live yet").
export async function apiListColleges(): Promise<College[]> {
	const res = await request<{ colleges: College[] }>('/colleges', { cacheTtlMs: 5 * 60 * 1000 });
	return res?.colleges ?? [];
}

// --- Moderation: block / unblock / report ----------------------------------
export type ReportTargetType = 'user' | 'post' | 'comment' | 'message';

export async function apiBlockUser(userId: string): Promise<{ ok: boolean; blocked: boolean }> {
	return request(`/users/${userId}/block`, { method: 'POST', auth: true });
}

export async function apiUnblockUser(userId: string): Promise<{ ok: boolean; blocked: boolean }> {
	return request(`/users/${userId}/block`, { method: 'DELETE', auth: true });
}

export async function apiListBlocked(): Promise<{ items: Array<{ id: string; displayName: string; profileImage: string | null; blockedAt: string }> }> {
	return request('/users/blocked', { auth: true });
}

export async function apiReport(input: {
	targetType: ReportTargetType;
	targetId: string;
	reason: string;
	details?: string;
}): Promise<{ ok: boolean; id?: string }> {
	return request('/reports', { method: 'POST', auth: true, body: input });
}

// --- Notifications ---------------------------------------------------------
export type NotificationItem = {
	id: string;
	type: string;
	entityType: string | null;
	entityId: string | null;
	message: string | null;
	read: boolean;
	createdAt: string;
	actor: { id: string; displayName: string; profileImage: string | null } | null;
};

export async function apiGetNotifications(cursor?: string, take = 20): Promise<{ items: NotificationItem[]; nextCursor: string | null }> {
	const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}&take=${take}` : `?take=${take}`;
	return request(`/notifications${qs}`, { auth: true });
}

export async function apiNotificationUnreadCount(): Promise<{ unread: number }> {
	return request('/notifications/unread-count', { auth: true, cacheTtlMs: 10000, dedupe: true });
}

export async function apiMarkNotificationsRead(): Promise<{ ok: boolean; updated?: number }> {
	return request('/notifications/read', { method: 'POST', auth: true }).then((resp: any) => {
		// Flush the cached unread-count GET (cacheTtlMs 10s) so the reconcile read after
		// marking-read returns the fresh 0 instead of the stale pre-mark value.
		invalidateApiGetCacheByPath('/notifications');
		return resp;
	});
}

export async function apiMarkNotificationRead(id: string): Promise<{ ok: boolean }> {
	return request(`/notifications/${id}/read`, { method: 'POST', auth: true }).then((resp: any) => {
		invalidateApiGetCacheByPath('/notifications');
		return resp;
	});
}

// --- Campus events ---------------------------------------------------------
export type EventItem = {
	id: string;
	title: string;
	description: string | null;
	location: string | null;
	startsAt: string;
	endsAt: string | null;
	creator: { id: string; displayName: string; profileImage: string | null } | null;
	counts: { going: number; interested: number };
	myRsvp: 'going' | 'interested' | null;
	createdAt: string;
};

export async function apiListEvents(
	scope: 'upcoming' | 'past' | 'all' = 'upcoming',
	cursor?: string,
	take = 20,
): Promise<{ items: EventItem[]; nextCursor: string | null }> {
	const params = new URLSearchParams({ scope, take: String(take) });
	if (cursor) params.set('cursor', cursor);
	return request(`/events?${params.toString()}`, { auth: true });
}

export async function apiCreateEvent(input: {
	title: string;
	startsAt: string;
	endsAt?: string | null;
	description?: string;
	location?: string;
}): Promise<EventItem> {
	return request('/events', { method: 'POST', auth: true, body: input });
}

export async function apiGetEvent(id: string): Promise<EventItem> {
	return request(`/events/${id}`, { auth: true });
}

export async function apiRsvpEvent(id: string, status: 'going' | 'interested'): Promise<{ ok: boolean; status: string }> {
	return request(`/events/${id}/rsvp`, { method: 'POST', auth: true, body: { status } });
}

export async function apiCancelRsvp(id: string): Promise<{ ok: boolean }> {
	return request(`/events/${id}/rsvp`, { method: 'DELETE', auth: true });
}

export async function apiDeleteEvent(id: string): Promise<{ ok: boolean }> {
	return request(`/events/${id}`, { method: 'DELETE', auth: true });
}

export async function apiMe(): Promise<any> {
	return request('/auth/me', { auth: true });
}

export type LegalStatusResponse = {
	requiresAcceptance: boolean;
	currentVersions: {
		termsVersion: string;
		privacyVersion: string;
		cookieVersion: string;
	};
	acceptedVersions: null | {
		termsVersion: string;
		privacyVersion: string;
		cookieVersion: string;
		acceptedAt: string;
	};
};

export async function apiGetLegalStatus(): Promise<LegalStatusResponse> {
	return request<LegalStatusResponse>('/legal/status', { auth: true });
}

export async function apiAcceptLegalDocuments(versions?: {
	termsVersion: string;
	privacyVersion: string;
	cookieVersion: string;
}) {
	return request('/legal/accept', {
		method: 'POST',
		auth: true,
		body: {
			agree: true,
			termsVersion: versions?.termsVersion || LEGAL_TERMS_VERSION,
			privacyVersion: versions?.privacyVersion || LEGAL_PRIVACY_VERSION,
			cookieVersion: versions?.cookieVersion || LEGAL_COOKIE_VERSION,
		},
	});
}

export async function apiGetCookieConsent(): Promise<{
	essential: boolean;
	analytics: boolean;
	marketing: boolean;
	updatedAt: string | null;
}> {
	return request('/legal/cookies', { auth: true });
}

export async function apiUpdateCookieConsent(input: { analytics: boolean; marketing: boolean }) {
	return request('/legal/cookies', { method: 'PUT', auth: true, body: input });
}

// Posts
export async function apiListPosts(
	globalOnly?: boolean,
	cursor?: string | null,
	take = 12
): Promise<{ success: boolean; data: any[]; nextCursor?: string | null }> {
	const qs = new URLSearchParams();
	if (globalOnly) qs.set('globalOnly', '1');
	if (cursor) qs.set('cursor', cursor);
	if (take) qs.set('take', String(take));
	return request(`/posts${qs.toString() ? `?${qs}` : ''}`, { auth: true });
}

export async function apiGetPost(postId: string): Promise<{ success: boolean; data: any }> {
	return request(`/posts/${encodeURIComponent(postId)}`, { auth: true });
}

export async function apiCreatePost(content: string, imageUrl?: string | null): Promise<any> {
	return request('/posts', { method: 'POST', auth: true, body: { content, ...(imageUrl ? { imageUrl } : {}) } });
}

export async function apiToggleLike(postId: string): Promise<{ success: boolean; data: string[] }> {
	return request(`/posts/${postId}/like`, { method: 'POST', auth: true });
}

export async function apiAddComment(postId: string, body: { content: string; parentId?: string }, opts?: { idempotencyKey?: string }): Promise<any> {
	const headers = opts?.idempotencyKey ? { 'X-Idempotency-Key': opts.idempotencyKey } : undefined;
	return request(`/posts/${postId}/comments`, { method: 'POST', auth: true, body, headers });
}

export async function apiDeletePost(postId: string): Promise<null> {
	return request(`/posts/${postId}`, { method: 'DELETE', auth: true });
}

// Comments
export async function apiDeleteComment(commentId: string): Promise<null | { softDeleted?: boolean }> {
	return request(`/comments/${commentId}`, { method: 'DELETE', auth: true });
}

// Users
export async function apiGetUserById(userId: string): Promise<{ success: boolean; data: any }> {
	return request(`/users/${userId}`, { auth: true });
}

export async function apiBanUser(userId: string): Promise<{ success: boolean; bannedUserId: string; summary: any }> {
	return request(`/users/${userId}/ban`, { method: 'DELETE', auth: true });
}

export {};

// Messages (DM) APIs
export const apiGetThreads = (): Promise<any[]> => request<any[]>(`/messages/threads`, {
	auth: true,
	cacheTtlMs: 15000,
	dedupe: true,
});
export const apiGetMessages = (peerId: string, cursor?: string, take = 25) =>
	request(`/messages/${peerId}${cursor ? `?cursor=${encodeURIComponent(cursor)}&take=${take}` : `?take=${take}`}`, { auth: true });
export const apiSendMessage = (peerId: string, content: string, imageUrl?: string | null) =>
	request(`/messages/${peerId}`, { method: 'POST', auth: true, body: { content, ...(imageUrl ? { imageUrl } : {}) } });
export const apiMarkRead = (peerId: string) => request(`/messages/${peerId}/read`, { method: 'POST', auth: true });
export const apiDeleteMessage = (messageId: string) => request(`/messages/message/${messageId}`, { method: 'DELETE', auth: true });

// Courses
export type CourseListItemDto = {
	id: string;
	subject: string;
	number: string;
	code: string;
	title: string;
	description?: string | null;
	credits?: number | null;
	memberCount: number;
	joined: boolean;
};
export type ListCoursesResp = {
	items: CourseListItemDto[];
	nextCursor?: string | null;
	page?: number;
	take?: number;
	total?: number;
	totalPages?: number;
	hasMore?: boolean;
};
export const apiListCourses = (q?: string, cursor?: string, take = 24, signal?: AbortSignal, page?: number) => {
	const params = new URLSearchParams();
	if (q) params.set('q', q);
	if (cursor) params.set('cursor', cursor);
	params.set('take', String(take));
	if (page) params.set('page', String(page));
	const qs = params.toString();
	return request<ListCoursesResp>(`/courses${qs ? `?${qs}` : ''}`, { auth: true, signal });
};
export const apiJoinCourse = (id: string) => request(`/courses/${id}/join`, { method: 'POST', auth: true }).then((resp) => {
	invalidateApiGetCacheByPrefixes(['/courses/joined', '/courses?']);
	return resp;
});
export const apiLeaveCourse = (id: string) => request(`/courses/${id}/leave`, { method: 'DELETE', auth: true }).then((resp) => {
	invalidateApiGetCacheByPrefixes(['/courses/joined', '/courses?']);
	return resp;
});
export const apiListCoursePosts = (id: string, cursor?: string, take = 25, signal?: AbortSignal) =>
	request(`/courses/${id}/posts${cursor ? `?cursor=${encodeURIComponent(cursor)}&take=${take}` : `?take=${take}`}`, {
		auth: true,
		signal,
		cacheTtlMs: !cursor && take <= 1 ? 120000 : 0,
		dedupe: !cursor && take <= 1,
	});
export const apiCreateCoursePost = (id: string, content: string) =>
	request(`/courses/${id}/posts`, { method: 'POST', auth: true, body: { content } }).then((resp) => {
		invalidateApiGetCacheByPath(`/courses/${id}/posts`);
		return resp;
	});

export type CourseMember = {
	id: string;
	displayName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	college?: string | null;
	major?: string | null;
	year?: number | null;
	avatarUrl?: string | null;
	joinedAt: string; // ISO date
};
export type ListCourseMembersResp = { items: CourseMember[]; nextCursor?: string | null };
export const apiListCourseMembers = (courseId: string, q?: string, cursor?: string, take = 25, signal?: AbortSignal) => {
	const params = new URLSearchParams();
	if (q) params.set('q', q);
	if (cursor) params.set('cursor', cursor);
	if (take) params.set('take', String(take));
	const qs = params.toString();
	return request<ListCourseMembersResp>(`/courses/${courseId}/members${qs ? `?${qs}` : ''}`, { auth: true, signal });
};

export type ClubListItemDto = {
	id: string;
	code: string;
	slug: string;
	name: string;
	category?: string | null;
	shortDesc?: string | null;
	description?: string | null;
	imageUrl?: string | null;
	profileUrl?: string | null;
	url?: string | null;
	memberCount: number;
	joined: boolean;
	createdAt?: string;
	updatedAt?: string;
};
export type ListClubsResp = {
	items: ClubListItemDto[];
	nextCursor?: string | null;
	page?: number;
	take?: number;
	total?: number;
	totalPages?: number;
	hasMore?: boolean;
};
export const apiListMyClubs = (signal?: AbortSignal) =>
	request<ListClubsResp>('/clubs/joined', {
		auth: true,
		signal,
		cacheTtlMs: 120000,
		dedupe: true,
	});
export const apiListClubPosts = (clubId: string, cursor?: string, take = 25, signal?: AbortSignal) =>
	request(`/clubs/${clubId}/posts${cursor ? `?cursor=${encodeURIComponent(cursor)}&take=${take}` : `?take=${take}`}`, {
		auth: true,
		signal,
		cacheTtlMs: !cursor && take <= 1 ? 120000 : 0,
		dedupe: !cursor && take <= 1,
	});
export const apiCreateClubPost = (clubId: string, content: string) =>
	request(`/clubs/${clubId}/posts`, { method: 'POST', auth: true, body: { content } }).then((resp) => {
		invalidateApiGetCacheByPath(`/clubs/${clubId}/posts`);
		return resp;
	});
export type ClubMember = {
	id: string;
	displayName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	major?: string | null;
	year?: number | null;
	avatarUrl?: string | null;
	joinedAt: string;
};
export type ListClubMembersResp = { items: ClubMember[]; nextCursor?: string | null };
export const apiListClubMembers = (clubId: string, q?: string, cursor?: string, take = 25, signal?: AbortSignal) => {
	const params = new URLSearchParams();
	if (q) params.set('q', q);
	if (cursor) params.set('cursor', cursor);
	if (take) params.set('take', String(take));
	const qs = params.toString();
	return request<ListClubMembersResp>(`/clubs/${clubId}/members${qs ? `?${qs}` : ''}`, { auth: true, signal });
};

// WebSocket base (optional consumers)
const WS_USING_PROXY = process.env.NODE_ENV === 'development' && !EXPLICIT_BASE;
export const WS_BASE = WS_USING_PROXY
	? 'http://127.0.0.1:5000'
	: (process.env.REACT_APP_WS_BASE || BASE_WITHOUT_API || '').replace(/\/$/, '');

// My courses
export async function apiListMyCourses(signal?: AbortSignal) {
	return request(`/courses/joined`, {
		method: 'GET',
		auth: true,
		signal,
		cacheTtlMs: 120000,
		dedupe: true,
	});
}

// Roommates
export type RoommateListingDTO = {
	id: string;
	title: string;
	description: string;
	location: string;
	moveInDate: string | null;
	budgetMin?: number | null;
	budgetMax?: number | null;
	budgetDisplay?: string;
	contactInfo?: string | null;
	smokingAllowed: boolean;
	petsAllowed: boolean;
	quietPreferred: boolean;
	lookingFor?: 'male' | 'female' | 'any';
	author: { id: string; displayName?: string | null; firstName?: string | null; lastName?: string | null };
	createdAt: string;
};
export type ListRoommatesResp = { items: RoommateListingDTO[]; nextCursor?: string | null };
export const apiListRoommates = (q?: string, cursor?: string, take = 20, signal?: AbortSignal) => {
	const params = new URLSearchParams();
	if (q) params.set('q', q);
	if (cursor) params.set('cursor', cursor);
	params.set('take', String(take));
	const qs = params.toString();
	return request<ListRoommatesResp>(`/roommates${qs ? `?${qs}` : ''}`, {
		auth: true,
		signal,
		cacheTtlMs: !cursor ? 60000 : 0,
		dedupe: !cursor,
	});
};
export const apiCreateRoommateListing = (body: {
	title: string;
	description: string;
	location: string;
	moveInDate: string;      // ISO
	budget: number;          // single value
	contactInfo: string;
	smokingAllowed: boolean;
	petsAllowed: boolean;
	quietPreferred: boolean;
	lookingFor: 'male' | 'female' | 'any';
}) => request<RoommateListingDTO>(`/roommates`, { method: 'POST', auth: true, body }).then((resp) => {
	invalidateApiGetCacheByPath('/roommates');
	return resp;
});
export const apiGetRoommateListing = (id: string) => request<RoommateListingDTO>(`/roommates/${id}`, { auth: true });

export async function apiUpdateRoommateListing(
	id: string,
	body: Partial<{ title: string; description: string; location: string; moveInDate: string; budget: number; contactInfo: string; smokingAllowed: boolean; petsAllowed: boolean; quietPreferred: boolean; lookingFor: 'male' | 'female' | 'any'; }>
): Promise<RoommateListingDTO> {
	const resp = await request<RoommateListingDTO>(`/roommates/listings/${id}`, { method: 'PUT', auth: true, body });
	invalidateApiGetCacheByPath('/roommates');
	return resp;
}

export async function apiDeleteRoommateListing(id: string): Promise<{ ok: true }> {
	const resp = await request<{ ok: true }>(`/roommates/listings/${id}`, { method: 'DELETE', auth: true });
	invalidateApiGetCacheByPath('/roommates');
	return resp;
}

// Messaging helper
export const apiGetOrCreateThreadWith = async (userId: string, context?: { type: 'roommate-listing'; listingId: string }) => {
	// We don't have a dedicated endpoint; fallback: ensure the thread exists by sending a noop marker message if needed is avoided.
	// Simplest approach: return a pseudo thread id as peerId; Inbox route uses /messages/:peerId
	return { id: userId };
};

// Students search
export type UserCard = {
	id: string;
	email?: string;
	displayName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	college?: string | null;
	major?: string | null;
	year?: number | null;
	hometown?: string | null;
	bio?: string | null;
	interests?: string[];
	clubs?: string[];
	avatarUrl?: string | null;
	createdAt?: string;
	updatedAt?: string;
};
export type ListUsersResp = { items: UserCard[]; nextCursor?: string | null };
export async function apiSearchUsers(q?: string, cursor?: string, take = 24, signal?: AbortSignal): Promise<ListUsersResp> {
	const params = new URLSearchParams();
	if (q) params.set('q', q);
	if (cursor) params.set('cursor', cursor);
	if (take) params.set('take', String(take));
	const qs = params.toString();
	return request<ListUsersResp>(`/users/search${qs ? `?${qs}` : ''}`, {
		auth: true,
		signal,
		cacheTtlMs: !q && !cursor ? 120000 : 30000,
		dedupe: !cursor,
	});
}

// Friends API
export type BasicUser = { id: string; displayName?: string | null; firstName?: string | null; lastName?: string | null; major?: string | null; year?: number | null; avatarUrl?: string | null; };
export type FriendRequestItem = { id: string; createdAt: string; from: BasicUser; to: BasicUser };
export type ListFriendRequestsResp = { items: FriendRequestItem[]; nextCursor?: string | null };
export type ListFriendsResp = { items: (BasicUser & { since?: string })[]; nextCursor?: string | null };
export async function apiSendFriendRequest(toUserId: string) {
	const resp = await request(`/friends/requests`, { method: 'POST', auth: true, body: { toUserId } });
	invalidateApiGetCacheByPrefixes(['/friends/requests', '/friends?', '/users/search']);
	return resp;
}
export async function apiListFriendRequests(box: 'incoming' | 'outgoing' = 'incoming', cursor?: string, take = 25, signal?: AbortSignal): Promise<ListFriendRequestsResp> {
	const p = new URLSearchParams({ box, take: String(take) });
	if (cursor) p.set('cursor', cursor);
	return request(`/friends/requests?${p.toString()}`, {
		auth: true,
		signal,
		cacheTtlMs: !cursor ? 60000 : 0,
		dedupe: !cursor,
	});
}
export async function apiAcceptFriendRequest(id: string) {
	const resp = await request(`/friends/requests/${id}/accept`, { method: 'POST', auth: true });
	invalidateApiGetCacheByPrefixes(['/friends/requests', '/friends?', '/users/search']);
	return resp;
}
export async function apiDeclineFriendRequest(id: string) {
	const resp = await request(`/friends/requests/${id}/decline`, { method: 'POST', auth: true });
	invalidateApiGetCacheByPath('/friends/requests');
	return resp;
}
export async function apiListFriends(q?: string, cursor?: string, take = 50, signal?: AbortSignal): Promise<ListFriendsResp> {
	const p = new URLSearchParams({ take: String(take) });
	if (q) p.set('q', q);
	if (cursor) p.set('cursor', cursor);
	return request(`/friends?${p.toString()}`, {
		auth: true,
		signal,
		cacheTtlMs: !cursor ? 60000 : 0,
		dedupe: !cursor,
	});
}
export async function apiDeleteFriend(userId: string) {
	const resp = await request(`/friends/${userId}`, { method: 'DELETE', auth: true });
	invalidateApiGetCacheByPrefixes(['/friends/requests', '/friends?', '/users/search']);
	return resp;
}

// Profile API
export async function apiUpdateProfile(data: {
  bio?: string;
  major?: string;
  year?: number;
  location?: string;
  hometown?: string;
  interests?: string[];
}): Promise<{ success?: boolean; user?: any; data?: any }> {
	return request<{ success?: boolean; user?: any; data?: any }>('/profile/me', { method: 'PUT', auth: true, body: data });
}

export async function apiGetMyProfile(): Promise<{ success?: boolean; user?: any; data?: any }> {
	return request<{ success?: boolean; user?: any; data?: any }>('/profile/me', { auth: true });
}

// Interests API
export async function apiGetInterests(): Promise<{ items: Interest[] }> {
  return request('/interests', { auth: true });
}

// Clubs API
export async function apiGetClubs(q?: string, cursor?: string, take = 24, page?: number, signal?: AbortSignal): Promise<ListClubsResp> {
	const params = new URLSearchParams();
	if (q) params.set('q', q);
	if (cursor) params.set('cursor', cursor);
	if (take) params.set('take', String(take));
	if (page) params.set('page', String(page));
	const qs = params.toString();
	return request(`/clubs${qs ? `?${qs}` : ''}`, {
		auth: true,
		signal,
		cacheTtlMs: !cursor ? 60000 : 0,
		dedupe: !cursor,
	});
}

export async function apiJoinClub(clubId: string) {
	const resp = await request(`/clubs/${clubId}/join`, { method: 'POST', auth: true });
	invalidateApiGetCacheByPrefixes(['/clubs/joined', '/clubs?']);
	return resp;
}

export async function apiLeaveClub(clubId: string) {
	const resp = await request(`/clubs/${clubId}/leave`, { method: 'DELETE', auth: true });
	invalidateApiGetCacheByPrefixes(['/clubs/joined', '/clubs?']);
	return resp;
}


// Professors API
export type ProfessorListItem = {
	id: string;
	name: string;
	department: string | null;
	courseCodes: string[];
	numReviews: number;
	avgRating: number | null;
	avgDifficulty: number | null;
	wouldTakeAgainPct: number | null;
	rmpRating: number | null;
	rmpNumRatings: number | null;
};

export async function apiListProfessors(
	opts: { q?: string; courseId?: string; cursor?: string; take?: number } = {},
	signal?: AbortSignal
): Promise<{ items: ProfessorListItem[]; nextCursor: string | null }> {
	const p = new URLSearchParams();
	if (opts.q) p.set('q', opts.q);
	if (opts.courseId) p.set('courseId', opts.courseId);
	if (opts.cursor) p.set('cursor', opts.cursor);
	p.set('take', String(opts.take ?? 30));
	return request(`/professors?${p.toString()}`, { auth: true, signal });
}

export async function apiGetProfessor(id: string, signal?: AbortSignal): Promise<any> {
	return request(`/professors/${id}`, { auth: true, signal });
}

export async function apiUpsertProfessorReview(
	id: string,
	data: { rating: number; difficulty?: number | null; wouldTakeAgain?: boolean | null; courseCode?: string | null; content?: string | null }
) {
	return request(`/professors/${id}/reviews`, { method: 'POST', auth: true, body: data });
}

export async function apiListCoursesForFilter(q = ''): Promise<{ id: string; code: string; title: string }[]> {
	const p = new URLSearchParams();
	if (q) p.set('q', q);
	p.set('take', '100');
	const resp: any = await request(`/courses?${p.toString()}`, { auth: true });
	return (resp?.items ?? []).map((c: any) => ({ id: c.id, code: c.code, title: c.title ?? c.name ?? c.code }));
}

export async function apiUploadAvatar(file: File): Promise<{ url: string }> {
	const token = localStorage.getItem('dragonsframe_token');
	const form = new FormData();
	form.append('file', file);
	const res = await fetch(`${API_BASE}${API_PREFIX}/uploads/avatar`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		body: form,
		credentials: 'include',
	});
	if (!res.ok) {
		const body: any = await res.json().catch(() => ({}));
		throw Object.assign(new Error(body?.error || 'upload_failed'), { code: res.status, info: body });
	}
	return res.json();
}

// Upload a generic image (post / chat attachment) and get back its URL.
export async function apiUploadImage(file: File): Promise<{ url: string }> {
	const token = localStorage.getItem('dragonsframe_token');
	const form = new FormData();
	form.append('file', file);
	const res = await fetch(`${API_BASE}${API_PREFIX}/uploads/image`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : undefined,
		body: form,
		credentials: 'include',
	});
	if (!res.ok) {
		const body: any = await res.json().catch(() => ({}));
		throw Object.assign(new Error(body?.error || 'upload_failed'), { code: res.status, info: body });
	}
	return res.json();
}
